import { useState } from "react";
import { BookMarked, BookmarkPlus, Trash2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/components/ui/use-toast";
import { api, extractErrorMessage } from "@/api/client";
import {
  useCreateSablona,
  useDeleteSablona,
  useSablony,
} from "@/api/hooks/useSablony";
import type { DataResponse, Sablona } from "@/api/schemas";

/**
 * Uložené konfigurácie Korpus Buildera (šablóny typu `korpus`).
 * Uloží aktuálne parametre + strom otvorov; načítanie ich vráti do
 * buildera — stačí upraviť rozmery a vytvoriť.
 */
export function KorpusPresety({
  getKonfig,
  onLoad,
}: {
  getKonfig: () => Record<string, unknown>;
  onLoad: (konfig: Record<string, unknown>) => void;
}): JSX.Element {
  const [saveOpen, setSaveOpen] = useState(false);
  const [loadOpen, setLoadOpen] = useState(false);
  const [nazov, setNazov] = useState("");

  const create = useCreateSablona();
  const remove = useDeleteSablona();
  const list = useSablony({ typ: "korpus" });
  const presety = list.data?.data ?? [];

  async function handleSave(): Promise<void> {
    try {
      await create.mutateAsync({
        nazov: nazov.trim(),
        typ: "korpus",
        data: getKonfig(),
      });
      toast({
        title: "Konfigurácia uložená",
        description: `„${nazov.trim()}" — načítaš ju cez Načítať.`,
      });
      setNazov("");
      setSaveOpen(false);
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Konfiguráciu sa nepodarilo uložiť",
        description: extractErrorMessage(err),
      });
    }
  }

  async function handleLoad(id: string, meno: string): Promise<void> {
    try {
      const { data } = await api.get<DataResponse<Sablona>>(
        `/sablony/${id}`,
      );
      onLoad(data.data.data as Record<string, unknown>);
      setLoadOpen(false);
      toast({ title: "Konfigurácia načítaná", description: meno });
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Konfiguráciu sa nepodarilo načítať",
        description: extractErrorMessage(err),
      });
    }
  }

  async function handleDelete(id: string, meno: string): Promise<void> {
    if (!window.confirm(`Zmazať konfiguráciu „${meno}"?`)) return;
    try {
      await remove.mutateAsync(id);
      toast({ title: "Konfigurácia zmazaná", description: meno });
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Chyba",
        description: extractErrorMessage(err),
      });
    }
  }

  return (
    <div className="flex items-center gap-1.5">
      <Button
        size="sm"
        variant="outline"
        onClick={() => setSaveOpen(true)}
        title="Uloží aktuálne parametre a delenie korpusu ako pomenovanú konfiguráciu"
      >
        <BookmarkPlus className="h-3.5 w-3.5" />
        Uložiť konfiguráciu
      </Button>
      <Button
        size="sm"
        variant="outline"
        onClick={() => setLoadOpen(true)}
        title="Načíta uloženú konfiguráciu korpusu do buildera"
      >
        <BookMarked className="h-3.5 w-3.5" />
        Načítať
      </Button>

      <Dialog open={saveOpen} onOpenChange={setSaveOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Uložiť konfiguráciu korpusu</DialogTitle>
            <DialogDescription className="sr-only">
              Uloženie aktuálneho nastavenia korpusu na opakované použitie.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1">
            <Label htmlFor="kp-nazov" className="text-xs">
              Názov konfigurácie
            </Label>
            <Input
              id="kp-nazov"
              value={nazov}
              onChange={(e) => setNazov(e.target.value)}
              placeholder="napr. spodná skrinka 600 HDF"
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setSaveOpen(false)}
              title="Zavrieť bez uloženia"
            >
              Zrušiť
            </Button>
            <Button
              onClick={() => void handleSave()}
              disabled={!nazov.trim() || create.isPending}
              title="Uloží konfiguráciu buildera"
            >
              {create.isPending ? "Ukladám…" : "Uložiť"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={loadOpen} onOpenChange={setLoadOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Načítať konfiguráciu</DialogTitle>
            <DialogDescription className="sr-only">
              Načítanie uloženej konfigurácie korpusu.
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-56 overflow-y-auto rounded border">
            {presety.length === 0 ? (
              <p className="p-3 text-xs text-muted-foreground">
                {list.isLoading
                  ? "Načítavam…"
                  : "Žiadne uložené konfigurácie."}
              </p>
            ) : (
              presety.map((p) => (
                <div
                  key={p.id}
                  className="flex cursor-pointer items-center justify-between gap-2 border-b px-3 py-1.5 text-sm last:border-b-0 hover:bg-muted"
                  onClick={() => void handleLoad(p.id, p.nazov)}
                >
                  <span className="min-w-0 truncate font-medium">
                    {p.nazov}
                  </span>
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-6 w-6 shrink-0 hover:text-destructive"
                    onClick={(e) => {
                      e.stopPropagation();
                      void handleDelete(p.id, p.nazov);
                    }}
                    aria-label={`Zmazať konfiguráciu ${p.nazov}`}
                    title="Zmazať konfiguráciu"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
